import {
  CarWheelMeasurementType,
  CarWheelMeasurementTypeLabels,
  CarWheelPositionText,
  CarWheelPositionTextLabels,
  InspectionItemKind,
  InspectionVehicleSideLocation,
  InspectionVehicleSideLocationLabels,
  type InspectionItemRecord
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { CarSideIcon, CarWheelPositions, type CarWheelPositionType, type NoddiIconName } from 'noddi-ui-common';
import { NavioListCard, NoddiCard, NoddiEmptyContent } from 'noddi-ui-web';
import { formatDecimalUpTo, MISSING_VALUE } from 'noddi-util';
import { treadDepthThresholdsFromLatest } from '../CarDetails/Cards/Inspection/wheelSetCarWheelMeasurements';
import { NavioTireSeasonChip } from '../CarWheel/NavioTireSeasonChip';
import { PhotoGrid } from './PhotoGrid';
import { SeasonWheelSet } from './SeasonWheelSet';
import { groupWheelSets } from './wheelSetGroups';

type Props = {
  items: InspectionItemRecord[];
  carId?: number;
};

const MEASUREMENT_TYPES = [
  CarWheelMeasurementType.TREAD_DEPTH,
  CarWheelMeasurementType.AIR_PRESSURE,
  CarWheelMeasurementType.TPMS_BATTERY_LEVEL
] as const;

const MEASUREMENT_ICON: Record<CarWheelMeasurementType, NoddiIconName> = {
  [CarWheelMeasurementType.TREAD_DEPTH]: 'Ruler',
  [CarWheelMeasurementType.AIR_PRESSURE]: 'Activity',
  [CarWheelMeasurementType.TPMS_BATTERY_LEVEL]: 'Info'
};

/** Left before right, front before rear, then the less common positions. */
const WHEEL_ORDER: readonly CarWheelPositionText[] = [
  CarWheelPositionText.FRONT_LEFT,
  CarWheelPositionText.FRONT_RIGHT,
  CarWheelPositionText.REAR_LEFT,
  CarWheelPositionText.REAR_RIGHT,
  CarWheelPositionText.FRONT,
  CarWheelPositionText.REAR,
  CarWheelPositionText.FRONT_LEFT_INNER,
  CarWheelPositionText.FRONT_RIGHT_INNER,
  CarWheelPositionText.REAR_LEFT_INNER,
  CarWheelPositionText.REAR_RIGHT_INNER,
  CarWheelPositionText.MIDDLE_FRONT_LEFT,
  CarWheelPositionText.MIDDLE_FRONT_RIGHT,
  CarWheelPositionText.MIDDLE_REAR_LEFT,
  CarWheelPositionText.MIDDLE_REAR_RIGHT,
  CarWheelPositionText.MIDDLE_FRONT_LEFT_INNER,
  CarWheelPositionText.MIDDLE_FRONT_RIGHT_INNER,
  CarWheelPositionText.MIDDLE_REAR_LEFT_INNER,
  CarWheelPositionText.MIDDLE_REAR_RIGHT_INNER
];

/** One block per wheel set season, then anything recorded without a season. */
export const InspectionObservations = ({ items, carId }: Props) => {
  const wheelSets = groupWheelSets(items);
  const seasonalIds = new Set(wheelSets.flatMap((group) => group.items.map((item) => item.id)));
  const unseasoned = items.filter((item) => !seasonalIds.has(item.id));
  const { data: treadDepthLatest } = noddiAsync.useGet({
    type: URLKeys.getLatestCarWheelMeasurements,
    input: { carId: carId ?? 0 },
    queryConfig: { enabled: carId != null && wheelSets.length > 0 }
  });

  if (items.length === 0) {
    return (
      <NoddiEmptyContent title='No observations recorded' message='Nothing was recorded in this session' size='small' />
    );
  }

  const photos = unseasoned.filter((item) => item.kind === InspectionItemKind.PHOTO);
  const photosWithImage = groupByLocation(photos.filter((item) => item.imageUrl)).flatMap((group) => group.items);
  const photosWithoutImage = photos.filter((item) => !item.imageUrl);
  const measurements = unseasoned.filter((item) => item.kind === InspectionItemKind.MEASUREMENT);
  const specifications = unseasoned.filter((item) => item.kind === InspectionItemKind.SPEC);
  const specificationSeason = sharedSeason(specifications);
  const skips = unseasoned.filter((item) => item.kind === InspectionItemKind.SKIP);
  const knownTypes = new Set<CarWheelMeasurementType>(MEASUREMENT_TYPES);
  const untypedMeasurements = measurements.filter(
    (item) => item.measurementType == null || !knownTypes.has(item.measurementType)
  );

  return (
    <div className='flex flex-col gap-5'>
      {wheelSets.length > 0 && (
        <div className='grid grid-cols-1 gap-4 xl:grid-cols-2'>
          {wheelSets.map((group) => (
            <SeasonWheelSet
              key={group.key}
              group={group}
              carId={carId}
              thresholds={treadDepthThresholdsFromLatest(treadDepthLatest, group.season)}
            />
          ))}
        </div>
      )}

      {photos.length > 0 && (
        <NoddiCard title='Photos' size='small' icon='Camera' iconTile titleCount={photos.length}>
          <div className='flex flex-col gap-4'>
            {photosWithImage.length > 0 && (
              <PhotoGrid
                className='lg:grid-cols-4'
                photos={photosWithImage.map((item) => ({
                  imageUrl: item.imageUrl ?? '',
                  label: locationLabel(item.location)
                }))}
              />
            )}
            {photosWithoutImage.map((item) => (
              <p key={item.id} className='text-sm text-muted-foreground'>
                {locationLabel(item.location)} photo is missing
              </p>
            ))}
            {photos
              .filter((item) => item.notes)
              .map((item) => (
                <p key={`note-${item.id}`} className='text-xs text-muted-foreground italic'>
                  {locationLabel(item.location)}: {item.notes}
                </p>
              ))}
          </div>
        </NoddiCard>
      )}

      {MEASUREMENT_TYPES.map((type) => {
        const typeItems = measurements.filter((item) => item.measurementType === type);
        if (typeItems.length === 0) {
          return null;
        }
        return (
          <MeasurementCard
            key={type}
            title={CarWheelMeasurementTypeLabels[type]}
            icon={MEASUREMENT_ICON[type]}
            items={typeItems}
          />
        );
      })}

      {untypedMeasurements.length > 0 && (
        <MeasurementCard title='Measurement' icon='Ruler' items={untypedMeasurements} />
      )}

      {specifications.length > 0 && (
        <NoddiCard
          title='Specifications'
          size='small'
          icon='ScanLine'
          iconTile
          titleCount={specifications.length}
          headerNode={specificationSeason ? <NavioTireSeasonChip season={specificationSeason} size='sm' /> : undefined}
        >
          <WheelReadings items={specifications} showSeason={specificationSeason == null} />
        </NoddiCard>
      )}

      {skips.length > 0 && (
        <NavioListCard
          title='Skipped'
          size='small'
          icon='CircleX'
          items={groupByLocation(skips).flatMap((group) => group.items)}
          getItemKey={(item) => item.id}
          getSearchText={(item) => `${locationLabel(item.location)} ${item.skipReason ?? ''} ${item.notes ?? ''}`}
          renderItem={(item) => (
            <div className='flex flex-col gap-1'>
              <span className='text-sm font-medium'>{locationLabel(item.location)}</span>
              <span className='text-sm text-muted-foreground'>{item.skipReason ? item.skipReason : 'Skipped'}</span>
              {item.notes ? <span className='text-sm text-muted-foreground italic'>{item.notes}</span> : null}
            </div>
          )}
        />
      )}
    </div>
  );
};

const MeasurementCard = ({
  title,
  icon,
  items
}: {
  title: string;
  icon: NoddiIconName;
  items: InspectionItemRecord[];
}) => {
  const season = sharedSeason(items);
  return (
    <NoddiCard
      title={title}
      size='small'
      icon={icon}
      iconTile
      titleCount={items.length}
      headerNode={season ? <NavioTireSeasonChip season={season} size='sm' /> : undefined}
    >
      <WheelReadings items={items} showSeason={season == null} />
    </NoddiCard>
  );
};

const WheelReadings = ({ items, showSeason }: { items: InspectionItemRecord[]; showSeason: boolean }) => (
  <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
    {groupByLocation(items).map((group) => {
      const position = wheelPosition(group.location);
      return (
        <div key={group.location ?? 'unspecified'} className='flex flex-col gap-4 rounded-lg border border-border p-4'>
          <div className='flex items-start gap-4'>
            {position ? <CarSideIcon carSide={position} width={20} height={33} /> : null}
            <p className='text-sm'>{locationLabel(group.location)}</p>
          </div>
          {group.items.map((item) => (
            <div key={item.id} className='flex flex-col gap-1'>
              <p className='text-sm font-medium'>{readingText(item)}</p>
              {showSeason && item.tire?.season ? <NavioTireSeasonChip season={item.tire.season} size='sm' /> : null}
              {item.kind === InspectionItemKind.MEASUREMENT && item.tire?.specification ? (
                <p className='text-xs text-muted-foreground'>{item.tire.specification}</p>
              ) : null}
              {conditionText(item) ? <p className='text-xs text-muted-foreground'>{conditionText(item)}</p> : null}
              {item.notes ? <p className='text-xs text-muted-foreground italic'>{item.notes}</p> : null}
            </div>
          ))}
        </div>
      );
    })}
  </div>
);

const readingText = (item: InspectionItemRecord): string => {
  if (item.kind === InspectionItemKind.SPEC) {
    return item.tire?.specification ?? MISSING_VALUE;
  }
  if (item.value) {
    return `${formatDecimalUpTo(item.value.value, 1)} ${item.value.unit}`;
  }
  return MISSING_VALUE;
};

const conditionText = (item: InspectionItemRecord): string => {
  const parts: string[] = [];
  if (item.tire?.hasTyreDamages) {
    parts.push('Tire damage');
  }
  if (item.tire?.hasRimDamages) {
    parts.push('Rim damage');
  }
  if (item.tire?.needsWheelAlignment) {
    parts.push('Needs alignment');
  }
  return parts.join(' · ');
};

const sharedSeason = (items: InspectionItemRecord[]) => {
  const seasons = items.flatMap((item) => (item.tire?.season ? [item.tire.season] : []));
  const unique = [...new Set(seasons)];
  return unique.length === 1 ? unique[0] : null;
};

type LocationGroup = {
  location: InspectionItemRecord['location'];
  items: InspectionItemRecord[];
};

const groupByLocation = (items: InspectionItemRecord[]): LocationGroup[] => {
  const groups = new Map<string, LocationGroup>();
  for (const item of [...items].sort((left, right) => left.id - right.id)) {
    const key = item.location ?? '';
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.set(key, { location: item.location, items: [item] });
    }
  }
  return [...groups.values()].sort(
    (left, right) =>
      locationRank(left.location) - locationRank(right.location) || (left.items[0]?.id ?? 0) - (right.items[0]?.id ?? 0)
  );
};

const locationRank = (location: InspectionItemRecord['location']): number => {
  if (!location) {
    return 1000;
  }
  const index = WHEEL_ORDER.indexOf(location as CarWheelPositionText);
  return index === -1 ? 500 : index;
};

const wheelPosition = (location: InspectionItemRecord['location']): CarWheelPositionType | undefined => {
  if (!location || !(location in CarWheelPositions)) {
    return undefined;
  }
  return location as CarWheelPositionType;
};

const locationLabel = (location: InspectionItemRecord['location']): string => {
  if (!location) {
    return 'Unspecified';
  }
  if (location in CarWheelPositionTextLabels) {
    return CarWheelPositionTextLabels[location as CarWheelPositionText];
  }
  if (location in InspectionVehicleSideLocationLabels) {
    return InspectionVehicleSideLocationLabels[location as InspectionVehicleSideLocation];
  }
  return location;
};
